import { useEffect, useRef, useState } from "react";
import type { PatchOp, PresetModel } from "@defibrillator/preset-core";
import type { VitalTargetVersion } from "@defibrillator/vital-adapter";
import { useConfirmation } from "@defibrillator/ui";
import type {
  AuthUser,
  LocalPresetRecord,
  PresetSaveResponse,
} from "../shared/types.js";
import { AuthScreen } from "./components/AuthScreen.js";
import { DevDebugPanel } from "./components/DevDebugPanel.js";
import { OnlineIndicator } from "./components/OnlineIndicator.js";
import { BrandWave, PulseWaveLoader } from "./components/PulseWaveLoader.js";
import { PwaSplashLoader, dismissBootSplash } from "./components/PwaSplashLoader.js";
import { StatusHint } from "./components/StatusHint.js";
import { PatchOpList } from "./components/PatchOpList.js";
import { PresetEditor } from "./components/PresetEditor.js";
import { PreviewPlayer } from "./components/PreviewPlayer.js";
import { PromptInput } from "./components/PromptInput.js";
import { UpdateToast } from "./components/UpdateToast.js";
import { VolumeKnob } from "./components/VolumeKnob.js";
import type { ClientAuthState } from "./lib/auth-state.js";
import { shouldMountWorkspace, shouldSyncLibrary } from "./lib/auth-state.js";
import { downloadVital } from "./lib/download-vital.js";
import {
  openEditorFromRecord,
  type PresetEditorSnapshot,
} from "./lib/editor-state.js";
import { listLocalPresets, saveLocalPreset, deleteLocalPreset } from "./lib/idb.js";
import {
  clearPendingLibraryDeletes,
  deleteLibraryPresetOnServer,
  formatLibraryDeleteRevertError,
  markPendingLibraryDeletes,
  mergeLibrarySnapshots,
} from "./lib/delete-library-preset.js";
import { fetchPreviewBlob, resolveLibraryPreview } from "./lib/library-preview.js";
import { syncLibraryFromServer } from "./lib/sync-library.js";
import { GenerationOptions } from "./components/GenerationOptions.js";
import {
  persistGenerationOptionsChange,
  readGenerationOptionsPanelOpen,
  readInitialGenerationOptions,
  writeGenerationOptionsPanelOpen,
} from "./lib/generation-options-panel.js";
import {
  buildLocalPresetRecord,
  runCreateGeneration,
  type CreateGenerationMode,
} from "./lib/run-create-generation.js";
import {
  DEFAULT_VITAL_TARGET_VERSION,
  composeGenerationPrompt,
  generationOptionsForRequest,
  sanitizeGenerationOptions,
  type PresetGenerationOptions,
} from "./lib/generation-options.js";
import { formatUserError } from "./lib/generation-status-labels.js";
import {
  CLIENT_VERSION_POLL_MS,
  checkForClientUpdate,
  invalidateClientAndReload,
} from "./lib/client-version.js";
import {
  buildEditorDiscardConfirmationCopy,
  buildLibraryDeleteConfirmationCopy,
  filterSnapshotIdsToPresentLibrary,
  snapshotLibraryPresetsByIds,
} from "./lib/library-delete-confirmation.js";

import { useDevDebugLog } from "./lib/use-dev-debug-log.js";

export function App() {
  const confirm = useConfirmation();
  const [auth, setAuth] = useState<ClientAuthState>({ phase: "checking" });
  const [prompt, setPrompt] = useState("warm analog pad with slow attack");
  const [generationOptions, setGenerationOptions] =
    useState<PresetGenerationOptions>(readInitialGenerationOptions);
  const [generationOptionsOpen, setGenerationOptionsOpen] = useState(
    readGenerationOptionsPanelOpen,
  );
  const [ops, setOps] = useState<PatchOp[]>([]);
  const [model, setModel] = useState<PresetModel | null>(null);
  const [modelVitalVersion, setModelVitalVersion] = useState<VitalTargetVersion>(
    DEFAULT_VITAL_TARGET_VERSION,
  );
  const [status, setStatus] = useState("idle");
  const [volume, setVolume] = useState(0.7);
  const [online, setOnline] = useState(navigator.onLine);
  const [createBusy, setCreateBusy] = useState<CreateGenerationMode["kind"] | null>(null);
  const creating = createBusy !== null;
  const [error, setError] = useState<string | null>(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [library, setLibrary] = useState<LocalPresetRecord[]>([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);
  const [libraryDeleteConfirmBusy, setLibraryDeleteConfirmBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [playingLibraryId, setPlayingLibraryId] = useState<string | null>(null);
  const [libraryLoadingId, setLibraryLoadingId] = useState<string | null>(null);
  const librarySelectAllRef = useRef<HTMLInputElement>(null);
  const [editor, setEditor] = useState<PresetEditorSnapshot | null>(null);
  const [editorDirty, setEditorDirty] = useState(false);
  const previewAbortRef = useRef<AbortController | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const createRequestRef = useRef(0);
  const [devDebug, setDevDebug] = useState(false);
  const { lines: debugLines, appendLocal: appendDebugLog, clear: clearDebugLog } =
    useDevDebugLog(devDebug);

  function setManagedPreviewUrl(url: string | null) {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (url?.startsWith("blob:")) {
      objectUrlRef.current = url;
    }
    setPreviewUrl(url);
  }

  function clearPrivateWorkspaceState() {
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
    setManagedPreviewUrl(null);
    setPlayingLibraryId(null);
    setLibraryLoadingId(null);
    setEditor(null);
    setEditorDirty(false);
    setOps([]);
    setModel(null);
    setModelVitalVersion(DEFAULT_VITAL_TARGET_VERSION);
    setLibrary([]);
    setSelectedLibraryIds([]);
    setCreateBusy(null);
    setError(null);
    setStatus("idle");
  }

  async function confirmDiscardEditor(): Promise<boolean> {
    if (!editor || !editorDirty) return true;
    const copy = buildEditorDiscardConfirmationCopy();
    return confirm({
      title: copy.title,
      description: copy.description,
      confirmLabel: copy.confirmLabel,
      tone: copy.tone,
    });
  }

  async function openEditor(record: LocalPresetRecord) {
    if (editor?.presetId === record.id) return;
    if (!(await confirmDiscardEditor())) return;
    const snapshot = openEditorFromRecord(record);
    if (!snapshot) {
      setError("This preset has no version metadata yet — sync or regenerate it first.");
      return;
    }
    setEditor(snapshot);
    setEditorDirty(false);
    setError(null);
  }

  async function onEditorSaved(saved: PresetSaveResponse, previewBlob?: Blob) {
    const existing = (await listLocalPresets()).find((p) => p.id === saved.id);
    const record: LocalPresetRecord = {
      id: saved.id,
      name: saved.name,
      style: saved.style,
      slug: saved.slug,
      prompt: existing?.prompt ?? "",
      ops: saved.ops,
      model: saved.model,
      updatedAt: saved.updatedAt,
      versionId: saved.versionId,
      contentHash: saved.contentHash,
      vitalVersion: saved.vitalVersion,
      generationId: existing?.generationId,
      jobId: undefined,
      previewBlob,
      previewContentHash: previewBlob ? saved.contentHash : undefined,
    };
    await saveLocalPreset(record);
    setLibrary(await listLocalPresets());
    setModel(saved.model);
    setModelVitalVersion(saved.vitalVersion);
    setOps(saved.ops);
    setEditor({
      presetId: saved.id,
      baseVersionId: saved.versionId,
      baseContentHash: saved.contentHash,
      savedModel: structuredClone(saved.model),
      prompt: record.prompt,
      slug: saved.slug,
      vitalVersion: saved.vitalVersion,
    });
    setEditorDirty(false);
  }

  async function onEditorRefined(result: {
    model: PresetModel;
    ops: LocalPresetRecord["ops"];
    versionId: string;
    contentHash?: string;
    vitalVersion?: VitalTargetVersion;
    jobId?: string;
    generationId: string;
    slug?: string;
  }) {
    if (!editor) return;
    const existing = (await listLocalPresets()).find((p) => p.id === editor.presetId);
    const record: LocalPresetRecord = {
      id: editor.presetId,
      name: result.model.name,
      style: result.model.style,
      slug: result.slug ?? editor.slug,
      prompt: existing?.prompt ?? editor.prompt,
      ops: result.ops,
      model: result.model,
      updatedAt: new Date().toISOString(),
      versionId: result.versionId,
      contentHash: result.contentHash,
      vitalVersion: result.vitalVersion ?? existing?.vitalVersion,
      generationId: result.generationId,
      jobId: result.jobId,
      previewBlob: undefined,
      previewContentHash: undefined,
    };
    await saveLocalPreset(record);
    setLibrary(await listLocalPresets());
    setModel(result.model);
    setModelVitalVersion(result.vitalVersion ?? existing?.vitalVersion ?? DEFAULT_VITAL_TARGET_VERSION);
    setOps(result.ops);
    setEditor({
      presetId: editor.presetId,
      baseVersionId: result.versionId,
      baseContentHash: result.contentHash,
      savedModel: structuredClone(result.model),
      prompt: record.prompt,
      slug: record.slug,
      vitalVersion: record.vitalVersion,
    });
    setEditorDirty(false);
    if (result.jobId) {
      void awaitPreview(result.jobId, editor.presetId);
    }
  }

  async function enterAuthenticated(user: AuthUser) {
    setAuth({ phase: "authenticated", user });
    try {
      setLibrary(await syncLibraryFromServer());
    } catch {
      try {
        setLibrary(await listLocalPresets());
      } catch {
        setLibrary([]);
      }
    }
  }

  function handleUnauthorized() {
    clearPrivateWorkspaceState();
    setAuth({ phase: "anonymous" });
  }

  useEffect(() => {
    if (auth.phase !== "checking") {
      dismissBootSplash();
    }
  }, [auth.phase]);

  useEffect(() => {
    void fetch("/api/config/public")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setDevDebug(Boolean(data?.devDebug));
      })
      .catch(() => setDevDebug(false));
  }, []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(async (data) => {
        if (cancelled) return;
        if (data?.user) {
          await enterAuthenticated(data.user as AuthUser);
        } else {
          setAuth({ phase: "anonymous" });
        }
      })
      .catch(() => {
        if (!cancelled) setAuth({ phase: "anonymous" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let cancelled = false;
    let updateInterval = 0;
    void import("virtual:pwa-register").then(({ registerSW }) => {
      if (cancelled) return;
      registerSW({
        immediate: true,
        onNeedRefresh() {
          setUpdateReady(true);
        },
        onRegisteredSW(_url, reg) {
          if (reg?.waiting) setUpdateReady(true);
          // Ask the browser to re-fetch sw.js on an interval; version.json is
          // the reliable deploy signal when a CDN sticks the old worker.
          if (!reg || cancelled) return;
          const tick = () => {
            void reg.update().catch(() => undefined);
          };
          tick();
          updateInterval = window.setInterval(tick, CLIENT_VERSION_POLL_MS);
          if (cancelled) window.clearInterval(updateInterval);
        },
      });
    }).catch(() => {
      // PWA plugin may be disabled in some test envs
    });
    return () => {
      cancelled = true;
      window.clearInterval(updateInterval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      if (cancelled || document.visibilityState === "hidden") return;
      const needsUpdate = await checkForClientUpdate();
      if (!cancelled && needsUpdate) setUpdateReady(true);
    }

    void poll();
    const id = window.setInterval(() => void poll(), CLIENT_VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  useEffect(() => {
    return () => {
      previewAbortRef.current?.abort();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const ids = new Set(library.map((preset) => preset.id));
    setSelectedLibraryIds((prev) => {
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [library]);

  useEffect(() => {
    const input = librarySelectAllRef.current;
    if (!input) return;
    const allSelected =
      library.length > 0 && selectedLibraryIds.length === library.length;
    input.indeterminate = selectedLibraryIds.length > 0 && !allSelected;
  }, [library.length, selectedLibraryIds.length]);

  async function onSignOut() {
    if (!(await confirmDiscardEditor())) return;
    clearPrivateWorkspaceState();
    try {
      await fetch("/api/auth/sign-out", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* still return to anonymous UI */
    }
    setAuth({ phase: "anonymous" });
  }

  async function awaitPreview(jobId: string, presetId?: string) {
    previewAbortRef.current?.abort();
    const ac = new AbortController();
    previewAbortRef.current = ac;
    setManagedPreviewUrl(null);
    setPlayingLibraryId(null);
    setStatus(`preview:${jobId.slice(0, 8)}`);
    try {
      const { blob, status: result } = await fetchPreviewBlob(jobId, {
        signal: ac.signal,
        onTick: (s) => setStatus(`preview:${s.state}`),
      });
      if (!result.preview.ready) return;

      setManagedPreviewUrl(URL.createObjectURL(blob));
      setStatus("preview:ready");

      if (presetId) {
        const current = (await listLocalPresets()).find((p) => p.id === presetId);
        if (current) {
          const updated: LocalPresetRecord = {
            ...current,
            jobId,
            previewBlob: blob,
            previewContentHash: current.contentHash,
            updatedAt: new Date().toISOString(),
          };
          await saveLocalPreset(updated);
          setLibrary(await listLocalPresets());
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus("preview:error");
    }
  }

  async function onPlayLibrary(record: LocalPresetRecord) {
    setError(null);
    if (playingLibraryId === record.id && previewUrl) {
      setManagedPreviewUrl(null);
      setPlayingLibraryId(null);
      setStatus("idle");
      return;
    }

    previewAbortRef.current?.abort();
    const ac = new AbortController();
    previewAbortRef.current = ac;
    setLibraryLoadingId(record.id);
    setStatus(`library:${record.name}`);

    try {
      const resolved = await resolveLibraryPreview(record, { signal: ac.signal });
      if (resolved.source === "renderer" || resolved.record.previewBlob) {
        setLibrary(await listLocalPresets());
      }
      setManagedPreviewUrl(resolved.url);
      setPlayingLibraryId(record.id);
      setStatus("preview:ready");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (err instanceof Error && /401|unauthorized/i.test(err.message)) {
        handleUnauthorized();
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setStatus("preview:error");
      setPlayingLibraryId(null);
    } finally {
      setLibraryLoadingId(null);
    }
  }

  async function deleteLibraryIds(ids: string[]) {
    if (ids.length === 0) return;

    const idSet = new Set(ids);
    const snapshots = library.filter((preset) => idSet.has(preset.id));
    if (snapshots.length === 0) return;

    markPendingLibraryDeletes(ids);

    if (playingLibraryId && idSet.has(playingLibraryId)) {
      previewAbortRef.current?.abort();
      setManagedPreviewUrl(null);
      setPlayingLibraryId(null);
    }
    if (editor && idSet.has(editor.presetId)) {
      setEditor(null);
      setEditorDirty(false);
    }
    setSelectedLibraryIds((prev) => prev.filter((id) => !idSet.has(id)));
    setLibrary((prev) => prev.filter((preset) => !idSet.has(preset.id)));

    try {
      try {
        await Promise.all(ids.map((id) => deleteLocalPreset(id)));
      } catch (err) {
        setLibrary((prev) => mergeLibrarySnapshots(prev, snapshots));
        setError(err instanceof Error ? err.message : String(err));
        return;
      }

      if (!shouldSyncLibrary(auth)) return;

      const failed: LocalPresetRecord[] = [];
      for (const id of ids) {
        const result = await deleteLibraryPresetOnServer(id);
        if (result.ok) continue;

        if (result.status === 401) {
          handleUnauthorized();
          return;
        }

        const snapshot = snapshots.find((preset) => preset.id === id);
        if (snapshot) failed.push(snapshot);
      }

      if (failed.length === 0) return;

      for (const record of failed) {
        await saveLocalPreset(record);
      }
      setLibrary((prev) => mergeLibrarySnapshots(prev, failed));
      setError(formatLibraryDeleteRevertError(failed));
    } finally {
      clearPendingLibraryDeletes(ids);
    }
  }

  async function confirmDeleteLibraryPresets(snapshot: LocalPresetRecord[]) {
    const copy = buildLibraryDeleteConfirmationCopy(snapshot);
    if (!copy) return;

    const confirmed = await confirm({
      title: copy.title,
      description: (
        <>
          <p style={{ margin: 0 }}>{copy.description}</p>
          {copy.previewItems.length > 0 ? (
            <ul>
              {copy.previewItems.map((item) => (
                <li key={item.id}>{item.name}</li>
              ))}
              {copy.remainingNameCount > 0 ? (
                <li>{`and ${copy.remainingNameCount} more`}</li>
              ) : null}
            </ul>
          ) : null}
        </>
      ),
      confirmLabel: copy.confirmLabel,
      tone: "danger",
    });
    if (!confirmed) return;

    const ids = filterSnapshotIdsToPresentLibrary(snapshot, library);
    await deleteLibraryIds(ids);
  }

  async function onDeleteLibrary(id: string) {
    if (libraryDeleteConfirmBusy) return;
    const snapshot = snapshotLibraryPresetsByIds(library, [id]);
    if (snapshot.length === 0) return;
    setLibraryDeleteConfirmBusy(true);
    try {
      await confirmDeleteLibraryPresets(snapshot);
    } finally {
      setLibraryDeleteConfirmBusy(false);
    }
  }

  async function onDeleteSelectedLibrary() {
    if (libraryDeleteConfirmBusy) return;
    const snapshot = snapshotLibraryPresetsByIds(library, selectedLibraryIds);
    if (snapshot.length === 0) return;
    setLibraryDeleteConfirmBusy(true);
    try {
      await confirmDeleteLibraryPresets(snapshot);
    } finally {
      setLibraryDeleteConfirmBusy(false);
    }
  }

  function toggleLibrarySelection(id: string) {
    setSelectedLibraryIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
    );
  }

  function toggleSelectAllLibrary() {
    setSelectedLibraryIds((prev) =>
      prev.length === library.length ? [] : library.map((preset) => preset.id),
    );
  }

  async function runCreation(mode: CreateGenerationMode) {
    setError(null);
    if (!online) {
      setError("Generation requires connectivity");
      return;
    }
    if (mode.kind === "typed" && !mode.prompt.trim()) return;
    if (!(await confirmDiscardEditor())) return;

    const requestId = createRequestRef.current + 1;
    createRequestRef.current = requestId;
    const idempotencyKey = crypto.randomUUID();
    const priorPrompt = prompt;

    setEditor(null);
    setEditorDirty(false);
    previewAbortRef.current?.abort();
    setCreateBusy(mode.kind);
    setStatus("connecting");
    setOps([]);
    setModel(null);
    setModelVitalVersion(DEFAULT_VITAL_TARGET_VERSION);
    setManagedPreviewUrl(null);
    setPlayingLibraryId(null);

    const ac = new AbortController();
    previewAbortRef.current = ac;

    try {
      const result = await runCreateGeneration({
        mode,
        idempotencyKey,
        signal: ac.signal,
        handlers: {
          onStatus: (message) => {
            if (createRequestRef.current !== requestId) return;
            setStatus(message);
            if (devDebug) appendDebugLog("status", message);
          },
          onDebugLog: devDebug
            ? (source, message) => {
                if (createRequestRef.current !== requestId) return;
                appendDebugLog(source, message);
              }
            : undefined,
          onPrompt: (generatedPrompt) => {
            if (createRequestRef.current !== requestId) return;
            setPrompt(generatedPrompt);
          },
          onOps: (nextOps) => {
            if (createRequestRef.current !== requestId) return;
            setOps(nextOps);
          },
          onModel: (nextModel) => {
            if (createRequestRef.current !== requestId) return;
            setModel(nextModel);
          },
          onError: (message) => {
            if (createRequestRef.current !== requestId) return;
            setError(message);
          },
        },
      });

      if (createRequestRef.current !== requestId) return;

      if (result.unauthorized) {
        handleUnauthorized();
        return;
      }
      if (result.error && !result.latestModel) {
        if (mode.kind === "random" && !result.generationPrompt) {
          setPrompt(priorPrompt);
        }
        return;
      }
      if (result.error) {
        return;
      }

      setModelVitalVersion(result.vitalVersion ?? DEFAULT_VITAL_TARGET_VERSION);

      const record = buildLocalPresetRecord({
        result,
        fallbackPrompt:
          mode.kind === "typed"
            ? composeGenerationPrompt(
                mode.prompt,
                sanitizeGenerationOptions(mode.options),
              )
            : priorPrompt,
      });
      if (record) {
        await saveLocalPreset(record);
        if (shouldSyncLibrary(auth)) {
          try {
            setLibrary(await syncLibraryFromServer());
          } catch {
            setLibrary(await listLocalPresets());
          }
        }
      }

      if (result.jobId && record) {
        void awaitPreview(result.jobId, record.id);
      }
    } catch (err) {
      if (createRequestRef.current !== requestId) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    } finally {
      if (createRequestRef.current === requestId) {
        setCreateBusy(null);
      }
    }
  }

  async function onGenerate() {
    const options = sanitizeGenerationOptions(generationOptions);
    await runCreation({
      kind: "typed",
      prompt: prompt.replace(/\s+/g, " ").trim(),
      options: generationOptionsForRequest(options),
    });
  }

  async function onRandomPreset() {
    const options = sanitizeGenerationOptions(generationOptions);
    await runCreation({
      kind: "random",
      options: generationOptionsForRequest(options),
    });
  }

  const user = auth.phase === "authenticated" ? auth.user : null;
  const allLibrarySelected =
    library.length > 0 && selectedLibraryIds.length === library.length;

  if (auth.phase === "checking") {
    return <PwaSplashLoader message="Checking session…" />;
  }

  return (
    <div className="app-shell">
      <header className="brand-bar">
        <div className="brand-lockup">
          <h1 className="brand">Defibrillator</h1>
          <BrandWave />
        </div>
        <div className="header-controls">
          {user ? (
            <div className="account-controls">
              <span className="auth-identity" title={user.email}>
                {user.name || user.email}
              </span>
              <button type="button" className="btn ghost btn-compact" onClick={() => void onSignOut()}>
                Sign out
              </button>
            </div>
          ) : null}
          <OnlineIndicator online={online} />
        </div>
      </header>

      {auth.phase === "anonymous" ? (
        <main className="workspace">
          <AuthScreen onAuthenticated={(u) => void enterAuthenticated(u)} />
        </main>
      ) : null}

      {shouldMountWorkspace(auth) ? (
        <>
          <main className="workspace">
            <section className="panel" aria-busy={creating}>
              <h2>Prompt</h2>
              <GenerationOptions
                value={generationOptions}
                open={generationOptionsOpen}
                disabled={creating}
                onChange={(next) => {
                  setGenerationOptions(next);
                  persistGenerationOptionsChange(next);
                }}
                onOpenChange={(open) => {
                  setGenerationOptionsOpen(open);
                  writeGenerationOptionsPanelOpen(open);
                }}
              />
              <PromptInput
                id="create-prompt"
                label="Preset prompt"
                value={prompt}
                placeholder="Describe a sound — pad, bass, lead…"
                disabled={creating}
                onChange={setPrompt}
              />
              <div className="row create-actions">
                <button
                  type="button"
                  className={`btn${createBusy === "typed" ? " is-loading" : ""}`}
                  disabled={creating || !prompt.trim()}
                  onClick={() => void onGenerate()}
                >
                  {createBusy === "typed" ? <PulseWaveLoader /> : null}
                  {createBusy === "typed" ? "Generating…" : "Generate"}
                </button>
                <button
                  type="button"
                  className={`btn ghost${createBusy === "random" ? " is-loading" : ""}`}
                  disabled={creating || !online}
                  onClick={() => void onRandomPreset()}
                >
                  {createBusy === "random" ? <PulseWaveLoader /> : null}
                  {createBusy === "random" ? "Generating…" : "Random preset"}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={!model}
                  onClick={() => {
                    if (model) downloadVital(model, modelVitalVersion);
                  }}
                >
                  Download .vital
                </button>
                <VolumeKnob value={volume} onChange={setVolume} />
                <StatusHint status={status} />
              </div>
              {previewUrl || status.startsWith("preview:") || status.startsWith("library:") ? (
                <div className="row">
                  <PreviewPlayer
                    url={previewUrl}
                    volume={volume}
                    onPlayingChange={(playing) => {
                      if (!playing) setPlayingLibraryId(null);
                    }}
                  />
                </div>
              ) : null}
              {error ? <p className="status-error">{formatUserError(error)}</p> : null}
            </section>

            <section className="panel">
              <h2>Patch ops</h2>
              <PatchOpList ops={ops} />
            </section>

            {editor ? (
              <PresetEditor
                snapshot={editor}
                online={online}
                volume={volume}
                onDirtyChange={setEditorDirty}
                onClose={() => {
                  setEditor(null);
                  setEditorDirty(false);
                }}
                onSaved={onEditorSaved}
                onRefined={onEditorRefined}
                onConflict={() => {
                  void syncLibraryFromServer()
                    .then(setLibrary)
                    .catch(() => undefined);
                }}
              />
            ) : null}

            <section className="panel">
              <div className="library-header">
                <h2>Library</h2>
                {library.length > 0 ? (
                  <div className="library-toolbar">
                    <label className="library-select-all">
                      <input
                        ref={librarySelectAllRef}
                        type="checkbox"
                        className="library-check"
                        checked={allLibrarySelected}
                        onChange={toggleSelectAllLibrary}
                      />
                      <span>All</span>
                    </label>
                    {selectedLibraryIds.length > 0 ? (
                      <button
                        type="button"
                        className="btn btn-compact btn-danger"
                        disabled={libraryDeleteConfirmBusy}
                        onClick={() => void onDeleteSelectedLibrary()}
                      >
                        Delete ({selectedLibraryIds.length})
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {library.length === 0 ? (
                <p style={{ margin: 0, color: "var(--ink-muted)" }}>No presets yet.</p>
              ) : (
                <ul className="ops">
                  {library.map((p) => {
                    const loading = libraryLoadingId === p.id;
                    const active = playingLibraryId === p.id && Boolean(previewUrl);
                    const selected = selectedLibraryIds.includes(p.id);
                    return (
                      <li key={p.id} className="library-row">
                        <label className="library-select">
                          <input
                            type="checkbox"
                            className="library-check"
                            checked={selected}
                            aria-label={`Select ${p.name}`}
                            onChange={() => toggleLibrarySelection(p.id)}
                          />
                        </label>
                        <span className="library-meta">
                          <strong>{p.name}</strong> · {p.style} · {new Date(p.updatedAt).toLocaleString()}
                          {p.previewBlob ? (
                            <span className="library-tag"> · cached</span>
                          ) : p.jobId ? (
                            <span className="library-tag"> · remote</span>
                          ) : (
                            <span className="library-tag"> · approx</span>
                          )}
                        </span>
                        <span className="library-actions">
                          <button
                            type="button"
                            className="btn ghost btn-compact"
                            disabled={loading}
                            title={
                              p.previewBlob
                                ? "Play cached preview"
                                : p.jobId
                                  ? "Load preview from renderer"
                                  : "Render approximate preview in browser"
                            }
                            onClick={() => void onPlayLibrary(p)}
                          >
                            {loading ? "…" : active ? "Stop" : "Play"}
                          </button>
                          <button
                            type="button"
                            className="btn ghost btn-compact"
                            onClick={() => void openEditor(p)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn ghost btn-compact"
                            onClick={() => downloadVital(p.model, p.vitalVersion)}
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            className="btn btn-compact btn-danger"
                            disabled={libraryDeleteConfirmBusy}
                            onClick={() => void onDeleteLibrary(p.id)}
                          >
                            Delete
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </main>

          <footer className="footer">
            <span>Approximate preview · listening caution on downloads</span>
            <span className="readout">
              {editor
                ? library.find((p) => p.id === editor.presetId)?.name ?? editor.savedModel.name
                : model
                  ? model.name
                  : "—"}
            </span>
          </footer>
        </>
      ) : null}

      {devDebug ? <DevDebugPanel lines={debugLines} onClear={clearDebugLog} /> : null}

      <UpdateToast
        visible={updateReady}
        onDismiss={() => setUpdateReady(false)}
        onUpdate={() => {
          void invalidateClientAndReload();
        }}
      />
    </div>
  );
}
